import * as React from "react"
import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu"
import { MixerHorizontalIcon } from "@radix-ui/react-icons"
import type { Table } from "@tanstack/react-table"
import { ArrowLeft, ArrowRight, ChevronDown, Eye, EyeOff, Pin, PinOff } from "lucide-react"

import { Button } from "../../../internal/primitives/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../../../internal/primitives/dropdown-menu"
import { cn } from "../../../lib/utils"

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>
}

function getColumnLabel(header: string) {
  return header.replace(/[_-]+/g, " ").replace(/\b\w/g, (character) => character.toUpperCase())
}

export function DataTableViewOptions<TData>({
  table,
}: DataTableViewOptionsProps<TData>) {
  const viewColumns = table
    .getAllColumns()
    .filter((column) => typeof column.accessorFn !== "undefined")

  const hasCustomLayout =
    Object.values(table.getState().columnVisibility).some((value) => value === false) ||
    Boolean(table.getState().columnPinning.left?.length || table.getState().columnPinning.right?.length) ||
    table.getState().columnOrder.length > 0 ||
    Object.keys(table.getState().columnSizing).length > 0

  // Fix #9: replace native <details>/<summary> with React-controlled disclosure state.
  // The <details> element had no animation, used browser-default triangle markers, and
  // broke the visual rhythm of the design system.
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set())
  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const moveColumn = (columnId: string, direction: "earlier" | "later") => {
    table.setColumnOrder((currentOrder) => {
      const orderedIds = currentOrder.length
        ? [...currentOrder]
        : table.getAllLeafColumns().map((column) => column.id)
      const currentIndex = orderedIds.indexOf(columnId)

      if (currentIndex < 0) {
        return orderedIds
      }

      const targetIndex =
        direction === "earlier" ? Math.max(currentIndex - 1, 0) : Math.min(currentIndex + 1, orderedIds.length - 1)

      if (targetIndex === currentIndex) {
        return orderedIds
      }

      const nextOrder = [...orderedIds]
      const [movedColumn] = nextOrder.splice(currentIndex, 1)
      nextOrder.splice(targetIndex, 0, movedColumn)
      return nextOrder
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-8 text-muted-foreground hover:text-foreground"
        >
          <MixerHorizontalIcon className="mr-2 h-3.5 w-3.5" />
          View
        </Button>
      </DropdownMenuTrigger>
      {/* Fix #8: replaced per-column cards (very tall with 10+ columns) with compact
          list rows. The primary show/hide toggle is always visible; pin/move/resize
          controls live behind a per-row chevron so they don't crowd the default view. */}
      <DropdownMenuContent align="end" className="w-[260px] p-0 shadow-lg">
        <div className="flex items-center justify-between px-3 py-2 bg-muted/20">
          <DropdownMenuLabel className="p-0 text-xs font-bold uppercase tracking-wider text-muted-foreground/80">Table layout</DropdownMenuLabel>
          {hasCustomLayout ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                table.resetColumnVisibility()
                table.resetColumnPinning()
                table.resetColumnOrder()
                table.resetColumnSizing()
                setExpandedIds(new Set())
              }}
              className="h-6 px-1.5 text-[10px] font-bold uppercase tracking-tight text-primary hover:bg-primary/10"
            >
              Reset
            </Button>
          ) : null}
        </div>
        <DropdownMenuSeparator className="m-0" />
        <div className="max-h-[24rem] overflow-y-auto p-1.5">
          {viewColumns.map((column) => {
            const label =
              typeof column.columnDef.header === "string"
                ? column.columnDef.header
                : getColumnLabel(column.id)
            const pinned = column.getIsPinned()
            const isExpanded = expandedIds.has(column.id)

            return (
              <div key={column.id} className="mb-0.5 last:mb-0">
                {/* Compact list row — show/hide icon + column name + layout expand chevron */}
                <div className={cn(
                  "flex items-center gap-1 rounded-md px-1 py-0.5 transition-colors",
                  isExpanded ? "bg-muted/40" : "hover:bg-muted/30"
                )}>
                  {/* Show/hide toggle */}
                  <button
                    type="button"
                    onClick={() => column.toggleVisibility(!column.getIsVisible())}
                    disabled={!column.getCanHide()}
                    aria-label={column.getIsVisible() ? `Hide ${label}` : `Show ${label}`}
                    className={cn(
                      "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md transition-colors",
                      "hover:bg-muted/50 disabled:pointer-events-none disabled:opacity-40",
                      column.getIsVisible() ? "text-foreground" : "text-muted-foreground/30"
                    )}
                  >
                    {column.getIsVisible()
                      ? <Eye className="h-3.5 w-3.5" />
                      : <EyeOff className="h-3.5 w-3.5" />}
                  </button>

                  {/* Column name + optional pin label */}
                  <div className="min-w-0 flex-1 px-1">
                    <p className={cn(
                      "truncate text-[11px] font-medium",
                      column.getIsVisible() ? "text-foreground" : "text-muted-foreground/50"
                    )}>
                      {label}
                    </p>
                    {pinned && (
                      <p className="text-[9px] font-bold uppercase tracking-tighter text-primary/70">
                        {pinned}
                      </p>
                    )}
                  </div>

                  {/* Layout controls expand/collapse */}
                  <button
                    type="button"
                    onClick={() => toggleExpanded(column.id)}
                    aria-label={`${isExpanded ? "Collapse" : "Expand"} layout options for ${label}`}
                    aria-expanded={isExpanded}
                    className={cn(
                      "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md transition-colors",
                      isExpanded ? "bg-muted text-foreground" : "text-muted-foreground/40 hover:bg-muted/50 hover:text-muted-foreground"
                    )}
                  >
                    <ChevronDown className={cn(
                      "h-3 w-3 transition-transform duration-200",
                      isExpanded && "rotate-180"
                    )} />
                  </button>
                </div>

                {/* Layout controls panel — only rendered when expanded */}
                {isExpanded && (
                  <div className="mt-1 flex flex-wrap gap-1 border-l-2 border-muted/50 ml-3 pl-2 pb-2">
                    <Button
                      type="button"
                      variant={pinned === "left" ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => column.pin(pinned === "left" ? false : "left")}
                      disabled={!column.getCanPin()}
                      className="h-6 px-1.5 text-[9px] font-bold uppercase tracking-tight"
                    >
                      {pinned === "left"
                        ? <><PinOff className="mr-1 h-2.5 w-2.5" />Unpin</>
                        : <><Pin className="mr-1 h-2.5 w-2.5" />Pin L</>}
                    </Button>
                    <Button
                      type="button"
                      variant={pinned === "right" ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => column.pin(pinned === "right" ? false : "right")}
                      disabled={!column.getCanPin()}
                      className="h-6 px-1.5 text-[9px] font-bold uppercase tracking-tight"
                    >
                      {pinned === "right"
                        ? <><PinOff className="mr-1 h-2.5 w-2.5" />Unpin</>
                        : <><Pin className="mr-1 h-2.5 w-2.5" />Pin R</>}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => moveColumn(column.id, "earlier")}
                      disabled={column.getIndex() <= 0}
                      className="h-6 px-1.5 text-[9px] font-bold uppercase tracking-tight"
                    >
                      <ArrowLeft className="mr-1 h-2.5 w-2.5" />
                      Move L
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => moveColumn(column.id, "later")}
                      disabled={column.getIndex() >= table.getAllLeafColumns().length - 1}
                      className="h-6 px-1.5 text-[9px] font-bold uppercase tracking-tight"
                    >
                      <ArrowRight className="mr-1 h-2.5 w-2.5" />
                      Move R
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => column.resetSize()}
                      className="h-6 px-1.5 text-[9px] font-bold uppercase tracking-tight text-muted-foreground hover:text-foreground"
                    >
                      Reset
                    </Button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
